/**
 * Tooltip + product-tour copy — single source of truth (web).
 *
 * MUST stay byte-identical with 2connect-mobile-app/src/tooltips/copy.ts.
 * When editing here, edit there too; the two files are manually synced until
 * a shared npm package makes the duplication worth removing.
 *
 * Conventions:
 *   • `title` ≤ 40 chars (renders in Radix Tooltip header / mobile bottom-sheet title)
 *   • `body` ≤ 240 chars (one to two sentences, active voice, no marketing fluff)
 *   • Keep technical specifics ("cosine pre-filter", "Phase 1 LLM score") out — users
 *     don't need pipeline details; explain the user-facing concept.
 *   • Scoring copy must not suggest the system is gameable.
 */

export type TooltipKey =
  | 'match-score-overall'
  | 'score-breakdown-role-fit'
  | 'score-breakdown-industry'
  | 'score-breakdown-stage'
  | 'score-breakdown-geography'
  | 'reciprocity-tier'
  | 'ai-summary'
  | 'dealbreakers'
  | 'synergy-areas'
  | 'friction-points'
  | 'express-interest'
  | 'block-user'
  | 'report-user'
  | 'engagement-score'
  | 'onboarding-progress'
  // Apr-24: onboarding guidance — per-slot explainers so users
  // can understand LLM-enum labels they never manually picked.
  | 'ai-summary-preview'
  | 'slot-primary-goal'
  | 'slot-user-type'
  | 'slot-industry-focus'
  | 'slot-geography'
  | 'slot-stage-preference'
  | 'slot-engagement-style'
  | 'slot-seeking-user-types'
  | 'slot-requirements'
  | 'slot-offerings'
  | 'slot-service-type'
  | 'slot-budget-range'
  | 'slot-role-type'
  | 'slot-seniority-level'
  | 'slot-remote-preference'
  | 'slot-compensation-range'
  | 'slot-skills-have'
  | 'slot-skills-need'
  | 'slot-commitment-level'
  | 'slot-equity-expectations'
  | 'slot-check-size'
  | 'slot-portfolio-size'
  | 'slot-investment-thesis'
  | 'slot-funding-need'
  | 'slot-funding-range'
  | 'slot-mentorship-areas'
  | 'slot-mentorship-format'
  | 'slot-mentorship-commitment'
  | 'slot-achievement'
  | 'slot-company-name'
  | 'slot-company-stage'
  | 'slot-team-size'
  | 'slot-experience-years'
  | 'slot-network-strength'
  // Apr-25: Match-detail Discover audit additions
  | 'match-tier-explainer'
  | 'discover-anonymous'
  // Apr-25: Discover card-level explainers
  | 'discover-urgency'
  | 'discover-activity'
  // Jun-18 (a user V1 test): Matches page filter tabs. "All 6 of these need
  // info tooltips, as a user I am confused." 3 status tabs + 3 Approved sub-tabs.
  | 'matches-tab-pending'
  | 'matches-tab-approved'
  | 'matches-tab-passed'
  | 'matches-subtab-all'
  | 'matches-subtab-mutual'
  | 'matches-subtab-waiting'
  | 'matches-subtab-passed-all'
  | 'matches-subtab-passed-by-me'
  | 'matches-subtab-passed-by-other';

export interface TooltipCopy {
  title: string;
  body: string;
}

export const TOOLTIPS: Record<TooltipKey, TooltipCopy> = {
  'match-score-overall': {
    title: 'Match score',
    body:
      "Overall compatibility combining role fit, industry, stage, geography, and an AI reasoning pass that reads both full profiles. Higher means more reciprocal value on paper.",
  },
  'score-breakdown-role-fit': {
    title: 'Role fit',
    body:
      "How well the other person's role and function line up with what you said you're looking for.",
  },
  'score-breakdown-industry': {
    title: 'Industry match',
    body:
      "Overlap between your focus industries and theirs. Higher for direct sector fit, moderate for adjacent sectors.",
  },
  'score-breakdown-stage': {
    title: 'Stage match',
    body:
      "Alignment of company stage, seniority, or funding stage. Relevant when one side is seeking a specific maturity level.",
  },
  'score-breakdown-geography': {
    title: 'Geography match',
    body:
      "Regional and timezone alignment. Full weight for shared regions, partial credit for compatible timezones.",
  },
  'reciprocity-tier': {
    title: 'Primary vs Adjacent',
    body:
      "Primary means you and this person are a direct fit for each other's goals. Adjacent means you are related but not a direct match, still worth a look.",
  },
  'ai-summary': {
    title: 'Your AI Summary',
    body:
      "Generated from your onboarding answers. This is what the matching system reads to find people for you. Editing it re-runs matching.",
  },
  'dealbreakers': {
    title: 'Dealbreakers',
    body:
      "Hard filters. Profiles that mention any of these are penalized heavily or excluded from your matches.",
  },
  'synergy-areas': {
    title: 'Synergy areas',
    body:
      "Specific ways the two of you can help each other, drawn from both profiles. Distinct from the overall summary.",
  },
  'friction-points': {
    title: 'Friction points',
    body:
      "Real gaps or tensions worth discussing upfront. Honest framing beats glossing over.",
  },
  'express-interest': {
    title: 'Express Interest',
    body:
      "Signals to the other person that you'd like to connect. If they reciprocate, you both see each other and can start a conversation.",
  },
  'block-user': {
    title: 'Block',
    body:
      "Hides this person from your matches, Discover, and any future recommendations. You can unblock from Settings.",
  },
  'report-user': {
    title: 'Report',
    body:
      "Flags this person to our moderation team. Used for inappropriate behavior, spam, or policy violations.",
  },
  'engagement-score': {
    title: 'Engagement',
    body:
      "Your activity level on the platform over the last 7 days. Updated daily.",
  },
  'onboarding-progress': {
    title: 'Onboarding progress',
    body:
      "You need six core dimensions filled to start matching. The richer your answers, the sharper your matches.",
  },
  // Apr-24: onboarding guidance ship
  'ai-summary-preview': {
    title: 'Your AI Summary',
    body:
      "Below is what the matching system extracted from our conversation. This becomes your profile: edit anything that looks off before we generate your matches.",
  },
  'slot-primary-goal': {
    title: 'Primary goal',
    body:
      "What you want to accomplish here, e.g. Raise Funding, Find Co-founder, Offer Services. Drives who reciprocates with you.",
  },
  'slot-user-type': {
    title: 'User type',
    body:
      "Your role in the ecosystem: Founder, Investor, Advisor, Service Provider, Job Seeker, etc. Determines which slots we fill.",
  },
  'slot-industry-focus': {
    title: 'Industry focus',
    body:
      "Sectors you care about: used for industry-level match scoring. Multiple industries allowed.",
  },
  'slot-geography': {
    title: 'Geography',
    body:
      "Region or timezone preference. Affects geography match score; 'None' means open to anywhere.",
  },
  'slot-stage-preference': {
    title: 'Stage preference',
    body:
      "Company stage or seniority level you engage with, e.g. Seed, Series A, or Staff+. Partial credit for adjacent stages.",
  },
  'slot-engagement-style': {
    title: 'Engagement style',
    body:
      "How you prefer to work with connections: hands-on, async, monthly check-ins, warm intros only, etc.",
  },
  'slot-seeking-user-types': {
    title: 'Who you want to meet',
    body:
      "The kind of people you want surfaced as matches, e.g. Founders, Investors, Advisors. Filters candidates before LLM scoring.",
  },
  'slot-requirements': {
    title: 'Requirements',
    body:
      "What you're actively looking to GET from connections: gaps you want filled, specific asks.",
  },
  'slot-offerings': {
    title: 'Offerings',
    body:
      "What you can PROVIDE to connections: skills, network, experience, specific value you bring.",
  },
  'slot-service-type': {
    title: 'Service type',
    body:
      "The categories of service you provide, e.g. Finance/Accounting, Engineering, Design, Legal.",
  },
  'slot-budget-range': {
    title: 'Budget range',
    body:
      "Pricing/engagement model: retainer, project rate, hourly, equity-based. Sets expectations upfront.",
  },
  'slot-role-type': {
    title: 'Role type',
    body:
      "The kind of role you're seeking, e.g. CTO, Head of Product, Senior Engineer.",
  },
  'slot-seniority-level': {
    title: 'Seniority level',
    body:
      "The seniority tier you're targeting: IC, Lead, Manager, Director, VP, C-level.",
  },
  'slot-remote-preference': {
    title: 'Remote preference',
    body:
      "Remote, hybrid, in-office, or flexible. Paired with geography for fit scoring.",
  },
  'slot-compensation-range': {
    title: 'Compensation range',
    body:
      "Expected salary/compensation band. Used to align with opportunities upfront.",
  },
  'slot-skills-have': {
    title: 'Skills you bring',
    body:
      "Strengths you personally bring to a co-founding or partnership setup.",
  },
  'slot-skills-need': {
    title: 'Skills you need',
    body:
      "Complementary skills you want in a co-founder or partner: the gap you can't fill yourself.",
  },
  'slot-commitment-level': {
    title: 'Commitment level',
    body:
      "What level of involvement you're expecting: full-time, part-time, nights & weekends.",
  },
  'slot-equity-expectations': {
    title: 'Equity expectations',
    body:
      "How you think about co-founder equity splits: 50/50, majority for existing founder, contribution-based.",
  },
  'slot-check-size': {
    title: 'Check size',
    body:
      "Typical investment amount per deal. Pairs with stage preference for deal-flow matching.",
  },
  'slot-portfolio-size': {
    title: 'Portfolio size',
    body:
      "How many companies you've invested in or manage: signals deal velocity and experience.",
  },
  'slot-investment-thesis': {
    title: 'Investment thesis',
    body:
      "The themes, sectors, or patterns you back. Short-form version of your investing lens.",
  },
  'slot-funding-need': {
    title: 'Funding need',
    body:
      "How much you're raising and for what. Informs which investors reciprocate.",
  },
  'slot-funding-range': {
    title: 'Funding range',
    body:
      "The range of the round you're raising: min and max check you'd accept.",
  },
  'slot-mentorship-areas': {
    title: 'Mentorship areas',
    body:
      "The domains where you want mentorship or can offer it, e.g. Fundraising, Go-to-Market, Hiring.",
  },
  'slot-mentorship-format': {
    title: 'Mentorship format',
    body:
      "How you like mentorship structured: weekly calls, async messages, ad-hoc, monthly sessions.",
  },
  'slot-mentorship-commitment': {
    title: 'Mentorship commitment',
    body:
      "Hours per month you're able to dedicate to mentorship: either as mentor or mentee.",
  },
  'slot-achievement': {
    title: 'Key achievement',
    body:
      "A concrete milestone or result: funding closed, team size scaled, revenue hit, exit achieved.",
  },
  'slot-company-name': {
    title: 'Company name',
    body:
      "Your current venture or employer. Used to anchor your profile; shown only in the edit view.",
  },
  'slot-company-stage': {
    title: 'Company stage',
    body:
      "Where your company is right now: pre-seed, seed, Series A, B+, profitable, etc.",
  },
  'slot-team-size': {
    title: 'Team size',
    body:
      "Headcount today. Signals both leverage and context for stage-appropriate matches.",
  },
  'slot-experience-years': {
    title: 'Years of experience',
    body:
      "Your relevant professional experience in years. Used for seniority-appropriate matching.",
  },
  'slot-network-strength': {
    title: 'Network strength',
    body:
      "The strength and breadth of your professional network in your industry/geography. Used as a soft-signal in matching reasoning, not a primary score input.",
  },
  // Apr-25: Match-detail Discover audit additions
  'match-tier-explainer': {
    title: 'Match tier',
    body:
      "Reflects compatibility band based on the LLM-judged score. Perfect (85+) = top reciprocal fit; Strong (70–84) = solid fit; Worth Exploring (55–69) = partial fit worth a look; Low (<55) = weak reciprocal fit.",
  },
  'discover-anonymous': {
    title: 'Anonymous browsing',
    body:
      "Names are hidden until both sides express interest. When you express interest, they're notified anonymously. If they reciprocate, you both see each other and can message.",
  },
  'discover-urgency': {
    title: 'Their urgency',
    body:
      "How time-sensitive their goal is. Urgent = closing soon, Time Sensitive = active push, Ongoing = open-ended, Exploring = early-stage thinking. Self-reported by the user.",
  },
  'discover-activity': {
    title: 'Activity level',
    body:
      "Based on platform activity in the last 7 days. Very active (70+) = recent logins + actions; Active (40–69) = checks in periodically; Less active (<40) = quiet recently.",
  },
  // Jun-18 (a user V1 test): Matches page filter tabs explained in plain words.
  'matches-tab-pending': {
    title: 'Pending',
    body:
      "New matches your AI agent found that you haven't reviewed yet. Open each one to see why it was matched, then Connect or Pass.",
  },
  'matches-tab-approved': {
    title: 'Approved',
    body:
      "Matches you chose to connect with. Split into Mutual (they said yes too) and Waiting on them (they haven't decided yet).",
  },
  'matches-tab-passed': {
    title: 'Passed',
    body:
      "Matches that aren't moving forward, either you passed on them or they passed on you. Kept here for your reference.",
  },
  'matches-subtab-all': {
    title: 'All',
    body:
      "Everyone you approved, whether or not the other person has responded yet.",
  },
  'matches-subtab-mutual': {
    title: 'Mutual',
    body:
      "Both you and the other person approved. You can message each other now.",
  },
  'matches-subtab-waiting': {
    title: 'Waiting on them',
    body:
      "You approved, but the other person hasn't decided yet. You can message once they approve too.",
  },
  'matches-subtab-passed-all': {
    title: 'All',
    body:
      "Every match that is no longer active, whether you passed on them or they passed on you.",
  },
  'matches-subtab-passed-by-me': {
    title: 'Passed by me',
    body:
      "Matches you decided not to pursue. They stay here for your reference.",
  },
  'matches-subtab-passed-by-other': {
    title: 'Passed by other',
    body:
      "Matches where the other person decided not to pursue. They stay here for your reference.",
  },
};

export type TourName = 'dashboard' | 'matches' | 'match-detail' | 'discover' | 'chat' | 'profile' | 'settings';

export interface TourStep {
  /** DOM selector or data-tour-id for the element this step anchors to. */
  target: string;
  title: string;
  body: string;
  /** 'bottom' | 'top' | 'left' | 'right' | 'center'. Web only; mobile uses auto. */
  placement?: 'bottom' | 'top' | 'left' | 'right' | 'center';
}

export const TOURS: Record<TourName, TourStep[]> = {
  dashboard: [
    {
      target: 'body',
      placement: 'center',
      title: 'Welcome to 2Connect Professional',
      body: 'Let us show you around in 30 seconds. You can replay this tour any time from Settings.',
    },
    {
      target: '[data-tour-id="dashboard-matches"]',
      placement: 'bottom',
      title: 'Your matches live here',
      body: 'New matches arrive daily. Tap any card to see why we matched you.',
    },
    {
      target: '[data-tour-id="dashboard-discover"]',
      placement: 'bottom',
      title: 'Discover is for browsing',
      body: 'Search or filter anonymously. Express Interest if someone looks like a fit.',
    },
    {
      target: '[data-tour-id="dashboard-profile"]',
      placement: 'left',
      title: 'Your AI Summary drives matching',
      body: 'Edit it from Profile any time to refine who you get matched with.',
    },
  ],
  matches: [
    {
      target: '[data-tour-id="match-score"]',
      placement: 'right',
      title: 'Compatibility score',
      body: 'How strong this match is overall, based on how well your goals fit each other.',
    },
    {
      target: '[data-tour-id="match-tier-pill"]',
      placement: 'bottom',
      title: 'Primary vs Adjacent',
      body: 'Primary means a direct fit for each other\'s goals. Adjacent means related, still worth a look.',
    },
    {
      target: '[data-tour-id="match-why"]',
      placement: 'top',
      title: 'Tap for the reasoning',
      body: 'See the headline, key points, synergies, and friction behind each match.',
    },
  ],
  'match-detail': [
    {
      target: '[data-tour-id="match-detail-headline"]',
      placement: 'bottom',
      title: "Why you're matched",
      body: 'Starts with a plain-English headline. Scroll for the full analysis.',
    },
    {
      target: '[data-tour-id="match-detail-breakdown"]',
      placement: 'top',
      title: 'Score breakdown',
      body: 'Each bar is a dimension of fit. Hover any bar for what it measures.',
    },
    {
      target: '[data-tour-id="match-detail-synergy"]',
      placement: 'top',
      title: 'Synergy + friction',
      body: 'Specific things you can help each other with, and honest gaps worth knowing.',
    },
    {
      target: '[data-tour-id="match-detail-start"]',
      placement: 'top',
      title: 'Ready to reach out?',
      body: "Start Conversation opens a chat. We suggest an opening line to break the ice.",
    },
  ],
  discover: [
    {
      target: '[data-tour-id="discover-search"]',
      placement: 'bottom',
      title: 'Search + filter',
      body: 'Browse by industry, role, or location. Names are hidden until both sides express interest.',
    },
    {
      target: '[data-tour-id="discover-card"]',
      placement: 'right',
      title: 'Anonymous until mutual',
      body: 'Tap a card to see an anonymized profile. Express Interest is how you signal.',
    },
    {
      target: '[data-tour-id="discover-interest"]',
      placement: 'top',
      title: 'Express Interest',
      body: "If they reciprocate, you both see each other and can message.",
    },
  ],
  chat: [
    {
      target: '[data-tour-id="chat-list"]',
      placement: 'right',
      title: 'Your conversations',
      body: 'All active threads. Latest activity floats to the top.',
    },
    {
      target: '[data-tour-id="chat-menu"]',
      placement: 'bottom',
      title: 'Safety controls',
      body: 'Block, Report, or Delete a conversation from the thread menu. Report flags moderation.',
    },
  ],
  // Apr-27: profile settings tours added per user directive after
  // mobile test confirmed no orientation existed for those surfaces. 5-tour
  // scope expanded to 7. Web copy will be mirrored in same shipment.
  profile: [
    {
      target: '[data-tour-id="profile-summary"]',
      placement: 'bottom',
      title: 'Your AI Summary drives matching',
      body: 'This is what we feed into matching every day. Edit it any time and your match list refreshes.',
    },
    {
      target: '[data-tour-id="profile-edit"]',
      placement: 'bottom',
      title: 'Edit details on the fly',
      body: 'Update name, photo, and contact info from the Edit Profile button. Your AI Summary updates separately.',
    },
    {
      target: '[data-tour-id="profile-account"]',
      placement: 'top',
      title: 'Account info at a glance',
      body: 'Member since, onboarding status, and your role. Reach Settings for password and notification controls.',
    },
  ],
  settings: [
    {
      target: '[data-tour-id="settings-list"]',
      placement: 'right',
      title: 'Account controls',
      body: 'Personal info, AI Summary, and password, each section opens inline.',
    },
    {
      target: '[data-tour-id="settings-tour-replay"]',
      placement: 'top',
      title: 'Replay any tour',
      body: 'Forgot how a screen works? Open Product Tour to replay any of the in-app tours.',
    },
  ],
};
